import type { InsuranceChecklist } from '../types';

export type ModelHazard = NonNullable<InsuranceChecklist['hazard']>;

export interface ModelGroup<T extends GroupableModel = InsuranceChecklist> {
  hazard: ModelHazard;
  label: string;
  models: T[];
}

type GroupableModel = Pick<
  InsuranceChecklist,
  'insurerName' | 'displayName' | 'hazard' | 'isDefault' | 'isRecommended'
>;

export const MODEL_GROUP_LABELS: Record<ModelHazard, string> = {
  wildfire: 'Wildfire',
  storm: 'Wind and storm',
  general: 'General',
};

const GROUP_ORDER: ModelHazard[] = ['wildfire', 'storm', 'general'];

/** Models with no hazard tag (e.g. agent-created) count as general. */
export function modelHazard(m: Pick<InsuranceChecklist, 'hazard'>): ModelHazard {
  return m.hazard ?? 'general';
}

function modelName(m: GroupableModel): string {
  return (m.displayName ?? m.insurerName).toLowerCase();
}

/**
 * Group preparation models by hazard for the picker. `leadHazard` goes first,
 * then wildfire, storm, general. Empty groups are dropped. Inside a group:
 * default, then recommended, then name (case-insensitive).
 */
export function groupModelsByHazard<T extends GroupableModel>(
  models: T[],
  leadHazard?: ModelHazard
): ModelGroup<T>[] {
  const order = leadHazard
    ? [leadHazard, ...GROUP_ORDER.filter((h) => h !== leadHazard)]
    : GROUP_ORDER;
  return order
    .map((hazard) => ({
      hazard,
      label: MODEL_GROUP_LABELS[hazard],
      models: models
        .filter((m) => modelHazard(m) === hazard)
        .sort(
          (a, b) =>
            Number(!!b.isDefault) - Number(!!a.isDefault) ||
            Number(!!b.isRecommended) - Number(!!a.isRecommended) ||
            modelName(a).localeCompare(modelName(b))
        ),
    }))
    .filter((g) => g.models.length > 0);
}
